import React, { useState, useEffect } from 'react';
import { Table, Input, Button, message, InputNumber, Spin } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { theme } from '../theme';
import { StandardCard } from './common';

interface TaraFardo {
  id: number;
  id_producto: number;
  producto: string;
  descripcion_producto: string;
  tara: number;
  denominacion: string;
  localidad: string;
  provincia: string;
  direccion: string;
}

interface Sucursal {
  id: number;
  denominacion: string;
  direccion: string;
  localidad: string;
  provincia: string;
}

interface DatosFijosResponse {
  valor_numerico: string | null;
}

const FardosConfig: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [messageApi, contextHolder] = message.useMessage();
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;
  
  // Estados para Tara de Fardos
  const [isLoadingTara, setIsLoadingTara] = useState(true);
  const [tarasFardos, setTarasFardos] = useState<TaraFardo[]>([]);
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [sucursal, setSucursal] = useState<Sucursal | null>(null);

  // Estados para Cantidad por Lote
  const [valorCLO, setValorCLO] = useState<number>(0);
  const [loadingCantidad, setLoadingCantidad] = useState<boolean>(true);
  const [actualizando, setActualizando] = useState<boolean>(false);

  // Cargar datos de Tara de Fardos
  useEffect(() => {
    const fetchData = async () => {
      if (!id_empresa || !id_sucursal) {
        console.log('FardosConfig: Esperando id_empresa y id_sucursal');
        return;
      }
      
      setIsLoadingTara(true);
      try {
        const token = await getAccessTokenSilently();

        // Cargar sucursal
        const sucursalUrl = `${process.env.REACT_APP_SUCURSAL_URL}/listar_sucursal/${id_empresa}/${id_sucursal}`;
        console.log('Cargando sucursal:', sucursalUrl);
        const sucursalResponse = await fetch(
          sucursalUrl,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (!sucursalResponse.ok) {
          const errorText = await sucursalResponse.text();
          console.error('Error HTTP al cargar sucursal:', sucursalResponse.status, errorText);
          throw new Error(`Error al cargar sucursal: ${sucursalResponse.status}`);
        }

        const sucursalData = await sucursalResponse.json();
        console.log('Sucursal cargada:', sucursalData);
        setSucursal(sucursalData);

        // Cargar taras
        const tarasUrl = `${process.env.REACT_APP_RELACION_URL}/listar_tara_fardo/${id_empresa}/${id_sucursal}`;
        console.log('Cargando taras:', tarasUrl);
        const tarasResponse = await fetch(
          tarasUrl,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (!tarasResponse.ok) {
          const errorText = await tarasResponse.text();
          console.error('Error HTTP al cargar taras:', tarasResponse.status, errorText);
          throw new Error(`Error al cargar taras: ${tarasResponse.status}`);
        }

        const tarasData = await tarasResponse.json();
        console.log('Taras cargadas:', tarasData);
        setTarasFardos(tarasData);

      } catch (error: any) {
        console.error('Error al cargar datos:', error);
        messageApi.error(error.message || 'Error al cargar los datos de tara');
      } finally {
        setIsLoadingTara(false);
      }
    };

    fetchData();
  }, [id_empresa, id_sucursal, getAccessTokenSilently]);

  // Cargar Cantidad por Lote
  useEffect(() => {
    const cargarDatosFijos = async () => {
      if (!id_empresa || !id_sucursal) {
        console.log('FardosConfig: Esperando id_empresa y id_sucursal para cantidad');
        return;
      }
      
      try {
        setLoadingCantidad(true);
        const token = await getAccessTokenSilently();
        console.log('Cargando cantidad por lote:', `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${id_empresa}/${id_sucursal}/CLO`);
        
        const response = await axios.get<DatosFijosResponse[]>(
          `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${id_empresa}/${id_sucursal}/CLO`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        console.log('Respuesta cantidad por lote:', response.data);
        const datosFijos = response.data[0];
        const valorNumerico = datosFijos?.valor_numerico;
        
        if (valorNumerico) {
          setValorCLO(parseFloat(valorNumerico));
        } else {
          setValorCLO(0);
        }
      } catch (error: any) {
        console.error('Error al cargar cantidad por lote:', error);
        console.error('Error details:', error.response?.data);
        messageApi.error(error.response?.data?.error || 'Error al cargar la cantidad por lote');
      } finally {
        setLoadingCantidad(false);
      }
    };

    cargarDatosFijos();
  }, [id_empresa, id_sucursal, getAccessTokenSilently]);

  const handleSaveTara = async (id: number) => {
    try {
      const tara = tarasFardos.find(t => t.id === id);
      if (!tara) return;

      const nuevoValor = parseFloat(editingValue);
      if (isNaN(nuevoValor) || nuevoValor < 0) {
        messageApi.error('Por favor ingrese un valor válido');
        setEditingKey(null);
        return;
      }

      const token = await getAccessTokenSilently();
      console.log('Actualizando tara:', { id_relacion: tara.id, id_producto: tara.id_producto, tara: nuevoValor });
      
      const response = await fetch(
        `${process.env.REACT_APP_RELACION_URL}/actualizar_tara_fardo/${id_empresa}/${id_sucursal}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            id_relacion: tara.id,
            id_producto: tara.id_producto,
            tara: nuevoValor
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error HTTP al actualizar tara:', response.status, errorText);
        throw new Error(`Error al actualizar: ${response.status}`);
      }

      setTarasFardos(prev =>
        prev.map(t => (t.id === id ? { ...t, tara: nuevoValor } : t))
      );

      messageApi.success('Tara actualizada correctamente');
      setEditingKey(null);
    } catch (error: any) {
      console.error('Error:', error);
      messageApi.error(error.message || 'Error al actualizar la tara');
    }
  };

  const handleActualizarCantidad = async () => {
    if (!id_empresa || !id_sucursal) return;

    try {
      setActualizando(true);
      const token = await getAccessTokenSilently();
      
      await axios.post(
        `${process.env.REACT_APP_RELACION_URL}/grabar_datos_fijos/${id_empresa}/${id_sucursal}/CLO/${'a'}/${valorCLO}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      messageApi.success('Cantidad actualizada correctamente');
    } catch (error) {
      console.error('Error al actualizar:', error);
      messageApi.error('Error al actualizar la cantidad');
    } finally {
      setActualizando(false);
    }
  };

  const columns = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Descripción</span>,
      dataIndex: 'descripcion_producto',
      key: 'descripcion_producto',
      width: '60%',
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text}</span>
      )
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Tara (Kg/Fardo)</span>,
      dataIndex: 'tara',
      key: 'tara',
      width: '40%',
      align: 'right' as const,
      render: (value: number, record: TaraFardo) => {
        if (editingKey === record.id) {
          return (
            <Input
              type="text"
              value={editingValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || value === '0' || value === '0.' || /^\d*\.?\d{0,2}$/.test(value)) {
                  setEditingValue(value);
                }
              }}
              onPressEnter={() => handleSaveTara(record.id)}
              onBlur={() => handleSaveTara(record.id)}
              autoFocus
              style={{ 
                width: '100px',
                textAlign: 'right'
              }}
            />
          );
        }
        return (
          <div 
            style={{ 
              cursor: 'pointer',
              color: theme.colors.textPrimary,
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onClick={() => {
              setEditingKey(record.id);
              setEditingValue(value.toString());
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {(value || 0).toFixed(2)}
          </div>
        );
      }
    }
  ];

  if (isLoadingTara || loadingCantidad) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div>
        {/* Sección 1: Tara por Producto */}
        <StandardCard
          title="Tara por Producto"
          style={{ marginBottom: '24px' }}
        >
          {sucursal && (
            <p style={{ 
              color: theme.colors.textSecondary, 
              fontSize: '14px',
              marginBottom: '16px',
              marginTop: 0
            }}>
              📍 {sucursal.denominacion} - {sucursal.localidad}, {sucursal.provincia}
            </p>
          )}
          <Table
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={tarasFardos}
            rowKey="id"
            pagination={false}
            size="middle"
          />
          <p style={{ 
            color: theme.colors.textSecondary, 
            fontSize: '13px',
            marginTop: '12px',
            marginBottom: 0,
            fontStyle: 'italic'
          }}>
            💡 Haz clic en cualquier valor de tara para editarlo
          </p>
        </StandardCard>

        {/* Sección 2: Cantidad por Lote */}
        <StandardCard
          title="Cantidad por Lote"
        >
          <p style={{ 
            color: theme.colors.textSecondary, 
            fontSize: '14px',
            marginBottom: '20px',
            marginTop: 0
          }}>
            Configuración default de fardos por lote
          </p>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <label 
              htmlFor="fardos-por-lote"
              style={{ 
                fontWeight: 500, 
                color: theme.colors.textPrimary,
                fontSize: '14px'
              }}
            >
              Fardos por lote:
            </label>
            <InputNumber
              id="fardos-por-lote"
              min={0}
              max={9999}
              value={valorCLO}
              onChange={(value) => setValorCLO(value || 0)}
              style={{ width: '120px' }}
              precision={2}
              aria-label="Cantidad de fardos por lote"
            />
            <Button
              type="primary"
              size="large"
              onClick={handleActualizarCantidad}
              loading={actualizando}
              style={{
                fontWeight: 500,
                minWidth: '120px'
              }}
            >
              {actualizando ? 'Actualizando...' : 'Actualizar'}
            </Button>
          </div>
        </StandardCard>
      </div>
    </>
  );
};

export default FardosConfig;

